import { ref, nextTick } from 'vue';
import { insertTextAtCaret } from '@/utils/insertTextAtCaret.js';

/**
 * Email 主旨／內文或僅內文（SMS）的變數插入：依最後聚焦欄位決定插入目標。
 * @param {import('vue').Reactive<object>} form - 需含 subject、bodyText、channel
 * @param {{
 *   wrapTag?: (key: string) => string,
 *   subjectFieldRef?: import('vue').Ref<HTMLInputElement|null>,
 *   bodyFieldRef?: import('vue').Ref<HTMLTextAreaElement|null>,
 * }} [options]
 *
 * 請在父層以 ref() 建立 subjectFieldRef／bodyFieldRef 並傳入，模板使用 ref="subjectFieldRef"，
 * 避免從 composable 解構 ref 時與模板 ref 綁定失效（Vue 3 常見問題）。
 */
export function useSubjectBodyVariableInsert(form, options = {}) {
  const subjectFieldRef = options.subjectFieldRef ?? ref(null);
  const bodyFieldRef = options.bodyFieldRef ?? ref(null);
  /** @type {import('vue').Ref<'subject'|'body'>} */
  const lastFocusedField = ref('body');

  const wrapTag = options.wrapTag || ((k) => `{{${k}}}`);

  function onSubjectFieldFocus() {
    lastFocusedField.value = 'subject';
  }

  function onBodyFieldFocus() {
    lastFocusedField.value = 'body';
  }

  async function doInsert(key) {
    const tag = wrapTag(key);
    const useSubject = form.channel === 'email' && lastFocusedField.value === 'subject';
    const el = useSubject ? subjectFieldRef.value : bodyFieldRef.value;
    if (!el) {
      return false;
    }
    if (useSubject) {
      return insertTextAtCaret(el, tag, (next) => {
        form.subject = next;
      });
    }
    return insertTextAtCaret(el, tag, (next) => {
      form.bodyText = next;
    });
  }

  /**
   * @param {string} key - 變數鍵（不含大括號）
   */
  async function insertVariableKey(key) {
    let ok = await doInsert(key);
    if (!ok) {
      await nextTick();
      ok = await doInsert(key);
    }
    return ok;
  }

  return {
    subjectFieldRef,
    bodyFieldRef,
    lastFocusedField,
    onSubjectFieldFocus,
    onBodyFieldFocus,
    insertVariableKey
  };
}
